import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from 'antd'
import { adminApi, adminLongRunningApi } from '../../api'
import type { ModelType } from './ModelProfileList'
import { SettingsSwitchList } from './settingsFormUi'
import './ModelSettings.css'

const TYPE_LABEL: Record<ModelType, string> = {
  llm: '大语言模型',
  embedding: '向量模型',
  multimodal: '多模态向量模型',
  rerank: '重排序模型',
}

type ProviderMeta = {
  id: string
  label: string
  provider_note?: string
  doc_url?: string
  api_key_hint?: string
  chat_models?: string[]
  embedding_models?: string[]
  requires_base_url?: boolean
}

type ProfilePayload = {
  id?: number
  profile_name: string
  model_name: string
  base_url: string
  embedding_dim: number | null
  description: string
  is_default: boolean
  is_active: boolean
  api_key?: string
  api_key_set?: boolean
  thinking_protocol?: ThinkingProtocol
  graph_extraction_thinking?: 'inherit' | 'off'
}

type ThinkingProtocol = 'inherit' | 'enable_thinking' | 'chat_template_kwargs' | 'deepseek'

type ExtractionTestResult = {
  ok: boolean
  message: string
  model: string
  endpoint_host: string | null
  thinking_status: 'service_default' | 'not_disabled' | 'zero_reported' | 'unconfirmed'
  elapsed_ms: number
  entity_count: number
  relation_count: number
  usage: { prompt_tokens: number | null; completion_tokens: number | null; reasoning_tokens: number | null } | null
  request_id: string | null
  local_override_active: boolean
}

const THINKING_STATUS = {
  service_default: '沿用原有行为',
  not_disabled: '仍返回思考内容',
  zero_reported: '接口报告思考用量为 0',
  unconfirmed: '思考状态未确认',
}

type Props = {
  open: boolean
  modelType: ModelType
  providerId: string | null
  mode: 'create' | 'edit'
  onClose: () => void
  onSaved: () => void
}

export function ModelProviderModal(props: Props) {
  return props.open ? (
    <ModelProviderModalContent key={`${props.modelType}:${props.providerId}:${props.mode}`} {...props} />
  ) : null
}

function ModelProviderModalContent({
  open,
  modelType,
  providerId,
  mode,
  onClose,
  onSaved,
}: Props) {
  const pid = (providerId || 'custom').trim().toLowerCase()
  const isCustomCreate = mode === 'create' && pid === 'custom'
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(!isCustomCreate)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [providerMeta, setProviderMeta] = useState<ProviderMeta | null>(
    isCustomCreate ? { id: 'custom', label: '自定义（OpenAI 兼容）' } : null,
  )
  const [profileId, setProfileId] = useState<number | null>(null)
  const [apiKeySet, setApiKeySet] = useState(false)
  const [savedConnection, setSavedConnection] = useState({ model_name: '', base_url: '' })
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<ExtractionTestResult | null>(null)
  const probeController = useRef<AbortController | null>(null)

  const chatModels = providerMeta?.chat_models || []
  const embeddingModels = providerMeta?.embedding_models || []
  const needsBaseUrl = pid === 'custom' || Boolean(providerMeta?.requires_base_url)
  const needsApiKey = pid !== 'local'
  const isReserved = modelType === 'multimodal' || modelType === 'rerank'

  useEffect(() => {
    if (isCustomCreate) return
    let active = true
    const controller = new AbortController()
    void adminApi.get<{
        profile: ProfilePayload | null
        provider: ProviderMeta
      }>(`/admin/model-profiles/by-provider/${modelType}/${pid}`, { signal: controller.signal }).then((r) => {
      if (!active) return
      const meta = r.data.provider
      setProviderMeta(meta)
      const p = r.data.profile
      setProfileId(p?.id ?? null)
      setApiKeySet(Boolean(p?.api_key_set))
      setSavedConnection({ model_name: p?.model_name || '', base_url: p?.base_url || '' })
      form.setFieldsValue({
        profile_name: p?.profile_name || meta.label,
        model_name:
          p?.model_name ||
          (modelType === 'llm'
            ? meta.chat_models?.[0] || ''
            : modelType === 'embedding'
              ? meta.embedding_models?.[0] || ''
              : ''),
        api_key: '',
        base_url: p?.base_url || '',
        embedding_dim: p?.embedding_dim ?? 1024,
        description: p?.description || '',
        is_default: p?.is_default ?? false,
        is_active: p?.is_active ?? true,
        thinking_protocol: p?.thinking_protocol || 'inherit',
        graph_extraction_thinking: p?.graph_extraction_thinking || 'inherit',
      })
    }).catch((e: unknown) => {
      if (!active) return
      setLoadFailed(true)
      message.error(e instanceof Error ? e.message : '加载失败')
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => {
      active = false
      controller.abort()
    }
  }, [isCustomCreate, modelType, pid, form])

  useEffect(() => () => probeController.current?.abort(), [])

  const watchedModelName = Form.useWatch('model_name', form) as string | undefined
  const watchedBaseUrl = Form.useWatch('base_url', form) as string | undefined
  const watchedApiKey = Form.useWatch('api_key', form) as string | undefined
  const connectionChanged = Boolean(watchedApiKey?.trim())
    || (watchedModelName || '').trim() !== savedConnection.model_name.trim()
    || (watchedBaseUrl || '').trim() !== savedConnection.base_url.trim()

  const chatModelOptions = [...new Set([...(watchedModelName ? [watchedModelName] : []), ...chatModels])]
    .map((m) => ({ value: m, label: m }))

  const embedModelOptions = [...new Set([...(watchedModelName ? [watchedModelName] : []), ...embeddingModels])]
    .map((m) => ({ value: m, label: m }))

  const testExtraction = async () => {
    if (!profileId || connectionChanged || testing || probeController.current) return
    const controller = new AbortController()
    probeController.current = controller
    try {
      const v = await form.validateFields(['thinking_protocol', 'graph_extraction_thinking'])
      if (controller.signal.aborted) return
      setTesting(true)
      setTestResult(null)
      const r = await adminLongRunningApi.post<ExtractionTestResult>(
        `/admin/model-profiles/${profileId}/test-extraction`,
        { thinking_protocol: v.thinking_protocol, graph_extraction_thinking: v.graph_extraction_thinking },
        { signal: controller.signal },
      )
      if (!controller.signal.aborted) setTestResult(r.data)
    } catch (e) {
      const err = e as { message?: string; errorFields?: unknown }
      if (!controller.signal.aborted && !err.errorFields) message.error(err.message || '测试失败')
    } finally {
      probeController.current = null
      if (!controller.signal.aborted) setTesting(false)
    }
  }

  const save = async () => {
    if (saving || testing || loading || loadFailed) return
    setSaving(true)
    try {
      const v = await form.validateFields()
      await adminApi.put(`/admin/model-profiles/by-provider/${modelType}/${pid}`, {
        profile_name: v.profile_name as string,
        ...((v.api_key as string)?.trim() ? { api_key: v.api_key as string } : {}),
        model_name: v.model_name as string,
        base_url: (v.base_url as string) || '',
        embedding_dim: modelType === 'embedding' ? (v.embedding_dim as number) : undefined,
        description: (v.description as string) || '',
        is_default: Boolean(v.is_default),
        is_active: Boolean(v.is_active),
        sort_no: 0,
        ...(modelType === 'llm' ? {
          thinking_protocol: v.thinking_protocol,
          graph_extraction_thinking: v.graph_extraction_thinking,
        } : {}),
      })
      message.success('已保存')
      onSaved()
      onClose()
    } catch (e) {
      const err = e as { message?: string; errorFields?: unknown }
      if (err?.message && !err.errorFields) message.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  const title =
    mode === 'create'
      ? `新建${TYPE_LABEL[modelType]}`
      : `${providerMeta?.label || pid}`

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width={640}
      destroyOnHidden
      className="model-provider-modal"
      footer={
        <div className="model-provider-modal-footer">
          <div className="model-provider-modal-actions">
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" loading={saving} disabled={loading || loadFailed || testing} onClick={() => void save()}>
              保存
            </Button>
          </div>
        </div>
      }
    >
      <Spin spinning={loading}>
        <>
          {isReserved && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="运行时接入预留"
              description="可先登记 API；当前生产链路尚未切换到此类型。"
            />
          )}
          {providerMeta?.provider_note && mode === 'edit' ? (
            <Typography.Paragraph type="secondary" className="model-provider-modal-note">
              {providerMeta.provider_note}
              {providerMeta.doc_url ? (
                <>
                  {' '}
                  <a href={providerMeta.doc_url} target="_blank" rel="noreferrer">
                    官方文档
                  </a>
                </>
              ) : null}
            </Typography.Paragraph>
          ) : null}

          <Form
            form={form}
            layout="vertical"
            disabled={loading || loadFailed || testing || saving}
            className="model-provider-modal-form"
            initialValues={{
              profile_name: isCustomCreate ? '自定义（OpenAI 兼容）' : '',
              model_name: '', api_key: '', base_url: '', embedding_dim: 1024,
              description: '', is_default: false, is_active: true,
              thinking_protocol: 'inherit', graph_extraction_thinking: 'inherit',
            }}
            onValuesChange={() => setTestResult(null)}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="profile_name" label="显示名称">
                  <Input placeholder={providerMeta?.label} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="model_name"
                  label="模型"
                  rules={
                    pid === 'local' ? [] : [{ required: true, message: '请填写或选择模型' }]
                  }
                >
                  {modelType === 'llm' && chatModelOptions.length > 0 && pid !== 'custom' ? (
                    <Select showSearch options={chatModelOptions} optionFilterProp="label" />
                  ) : modelType === 'embedding' && embedModelOptions.length > 0 && pid !== 'custom' ? (
                    <Select showSearch options={embedModelOptions} optionFilterProp="label" />
                  ) : (
                    <Input placeholder="模型 ID" disabled={pid === 'local'} />
                  )}
                </Form.Item>
              </Col>
              {modelType === 'embedding' ? (
                <Col xs={24} md={12}>
                  <Form.Item name="embedding_dim" label="向量维度">
                    <InputNumber min={64} max={4096} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              ) : null}
              {needsApiKey ? (
                <Col span={24}>
                  <Form.Item
                    name="api_key"
                    label="API Key"
                    rules={[{ required: !profileId, message: '请填写 API Key' }]}
                    extra={providerMeta?.api_key_hint || undefined}
                  >
                    <Input.Password placeholder={apiKeySet ? '已配置；留空继续使用现有 Key' : '保存后用于 API 调用'} />
                  </Form.Item>
                </Col>
              ) : null}
              <Col span={24}>
                <Form.Item
                  name="base_url"
                  label="API Base URL"
                  extra={needsBaseUrl ? '私有部署接入须填写 OpenAI 兼容根地址' : '留空则使用厂商默认地址'}
                  rules={
                    needsBaseUrl ? [{ required: true, message: '请填写 Base URL' }] : []
                  }
                >
                  <Input placeholder="https://api.example.com/v1" allowClear />
                </Form.Item>
              </Col>
            </Row>

            <SettingsSwitchList
              fields={[
                {
                  name: 'is_default',
                  label: '设为默认',
                  extra: '该类型下仅一个默认厂商，保存后同步到系统运行时',
                },
                {
                  name: 'is_active',
                  label: '启用',
                  extra: '关闭后运行时不会使用此厂商配置',
                  disabled: pid === 'local',
                },
              ]}
            />
            {modelType === 'llm' && pid !== 'local' ? (
              <Collapse
                ghost
                className="model-graph-settings"
                items={[{
                  key: 'graph', label: '图谱抽取设置',
                  children: <>
                    <Row gutter={16}>
                      <Col xs={24} sm={14}>
                        <Form.Item name="thinking_protocol" label="思考参数协议">
                          <Select options={[
                            { value: 'inherit', label: '沿用现有适配' },
                            { value: 'enable_thinking', label: '顶层开关（enable_thinking）' },
                            { value: 'chat_template_kwargs', label: '模板参数（chat_template_kwargs）' },
                            { value: 'deepseek', label: 'DeepSeek 协议（thinking）' },
                          ]} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={10}>
                        <Form.Item
                          name="graph_extraction_thinking" label="图谱抽取思考模式"
                          dependencies={['thinking_protocol']}
                          rules={[({ getFieldValue }) => ({
                            validator: (_, value: string) => value === 'off'
                              && getFieldValue('thinking_protocol') === 'inherit'
                              && !['qwen', 'deepseek'].includes(pid)
                              ? Promise.reject(new Error('请先选择网关支持的思考参数协议'))
                              : Promise.resolve(),
                          })]}
                        >
                          <Select options={[
                            { value: 'inherit', label: '沿用原有行为' },
                            { value: 'off', label: '关闭' },
                          ]} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <div className="model-extraction-test-action">
                      <Typography.Text type="secondary">
                        {!profileId ? '请先保存模型连接后测试。' : connectionChanged
                          ? '模型连接已修改，请先保存后测试。'
                          : '使用已保存连接和固定样本测试，可在保存前验证协议及模式。'}
                      </Typography.Text>
                      <Button
                        aria-label="测试抽取"
                        aria-busy={testing}
                        loading={testing}
                        disabled={!profileId || !apiKeySet || connectionChanged || loading || loadFailed || saving}
                        onClick={() => void testExtraction()}
                      >测试抽取</Button>
                    </div>
                    {testing ? <Typography.Paragraph type="secondary" className="model-extraction-test-hint">
                      正在等待模型响应。关闭窗口后可在服务端日志中查看执行结果。
                    </Typography.Paragraph> : null}
                    {testResult ? (
                      <div className="model-extraction-test-result" aria-live="polite">
                        <Alert
                          showIcon
                          type={!testResult.ok ? 'error' : ['not_disabled', 'unconfirmed'].includes(testResult.thinking_status) ? 'warning' : 'success'}
                          title={testResult.message}
                        />
                        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} colon={false}>
                          <Descriptions.Item label="测试范围">本档案测试</Descriptions.Item>
                          <Descriptions.Item label="耗时">{(testResult.elapsed_ms / 1000).toFixed(2)} 秒</Descriptions.Item>
                          <Descriptions.Item label="模型">{testResult.model}</Descriptions.Item>
                          <Descriptions.Item label="思考状态">{THINKING_STATUS[testResult.thinking_status]}</Descriptions.Item>
                          <Descriptions.Item label="抽取结果">{testResult.entity_count} 个实体 · {testResult.relation_count} 个关系</Descriptions.Item>
                          <Descriptions.Item label="Token 用量">
                            输入 {testResult.usage?.prompt_tokens ?? '未知'} / 输出 {testResult.usage?.completion_tokens ?? '未知'}
                          </Descriptions.Item>
                          <Descriptions.Item label="思考 Token">{testResult.usage?.reasoning_tokens ?? '未知'}</Descriptions.Item>
                          <Descriptions.Item label="接口">{testResult.endpoint_host || '—'}</Descriptions.Item>
                        </Descriptions>
                        <Typography.Paragraph type="secondary" className="model-extraction-test-hint">
                          {testResult.thinking_status === 'zero_reported'
                            ? '思考用量以接口报告为准，网关执行情况可结合请求号核对。'
                            : testResult.thinking_status === 'unconfirmed'
                              ? '接口未提供足够的思考用量信息，需结合网关记录确认。'
                              : testResult.thinking_status === 'not_disabled'
                                ? '关闭思考未确认生效，请核对网关参数透传和模型能力。'
                                : '测试结果用于检查模型连接和输出格式。'}
                          {testResult.local_override_active ? ' 当前存在本地模型覆盖，实际构建可能使用其他模型。' : ''}
                        </Typography.Paragraph>
                        {testResult.request_id ? <Typography.Text type="secondary" className="model-extraction-request-id" copyable>
                          {testResult.request_id}
                        </Typography.Text> : null}
                      </div>
                    ) : null}
                  </>,
                }]}
              />
            ) : null}
          </Form>
        </>
      </Spin>
    </Modal>
  )
}
